import * as React from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useStores } from '~store/utils';
import SiteStore from '~/modules/sites/store/Site';

const URL_PARAM = 'site';
// Routes whose path already encodes the site uuid: /sites/<uuid>/...
const PATH_SCOPED_RE = /^\/sites\/[^/]+/;

export const useSelectedSite = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { sites, permissions } = useStores();
  const isPathScoped = PATH_SCOPED_RE.test(location.pathname);
  const canViewSite = permissions.has('can_view_site');

  React.useEffect(() => {
    // Path encodes the site; siteWrapper drives selection. Strip any stale ?site=.
    if (isPathScoped) {
      if (searchParams.has(URL_PARAM)) {
        setSearchParams(
          (prev) => {
            const next = new URLSearchParams(prev);
            next.delete(URL_PARAM);
            return next;
          },
          { replace: true }
        );
      }
      return;
    }

    if (!canViewSite) {
      // No site permission → never expose ?site= in URL (clean stale ones too).
      if (searchParams.has(URL_PARAM)) {
        setSearchParams(
          (prev) => {
            const next = new URLSearchParams(prev);
            next.delete(URL_PARAM);
            return next;
          },
          { replace: true }
        );
      }
      return;
    }

    if (!sites.loaded || sites.sites.length === 0) return;

    const fromUrl = searchParams.get(URL_PARAM);
    if (fromUrl && sites.sites.some((s) => s.uuid === fromUrl)) {
      if (sites.selectedSiteUuid !== fromUrl) sites.setSelectedSiteUuid(fromUrl);
      return;
    }
    if (sites.selectedSiteUuid !== null) {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set(URL_PARAM, sites.selectedSiteUuid!);
          return next;
        },
        { replace: true }
      );
    }
  }, [
    isPathScoped,
    canViewSite,
    sites,
    sites.loaded,
    sites.sites.length,
    sites.selectedSiteUuid,
    searchParams,
    setSearchParams,
  ]);

  const setSelectedSiteUuid = React.useCallback(
    (uuid: string | null) => {
      sites.setSelectedSiteUuid(uuid);
      // On path-scoped pages, switching site means navigating to that site's root
      // (current path's nested ids — plane, etc. — won't exist on the new site).
      if (isPathScoped) {
        if (uuid) navigate(`/sites/${uuid}`);
        return;
      }
      if (!canViewSite) return;
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        if (uuid === null) next.delete(URL_PARAM);
        else next.set(URL_PARAM, uuid);
        return next;
      });
    },
    [sites, setSearchParams, isPathScoped, navigate, canViewSite]
  );

  return [sites.selectedSite as SiteStore | null, setSelectedSiteUuid] as const;
};
